import React from "react";
import { View, StyleSheet, TextInput, Text, Platform } from "react-native";
import LHSectionHeader from "../../components/LHSectionHeader";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface Props {
  value: string;
  onChange: (text: string) => void;
  error?: boolean;
  isDisabled?: boolean;
  testID?: string;
  isCompleted?: boolean;
}

const SparcWalkInspectionPosibleSolutions: React.FC<Props> = ({
  value,
  onChange,
  error = false,
  isDisabled = false,
  isCompleted = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  return (
    <View style={styles.wrapper}>
      {/* Card with integrated header and body (matches other SPARC components) */}
      <View style={styles.cardContainer}>
        <LHSectionHeader
          title="Possible solutions or changes to standards:"
          containerStyle={
            isDark
              ? { ...styles.sectionHeader, backgroundColor: colors.brand.primary }
              : styles.sectionHeader
          }
          textStyle={styles.header1text}
        />

        <View
          style={[
            styles.container,
            isDark && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
        >
          <TextInput
            placeholder="Please note any possible solutions or changes to standard you can think of based on your observations during your SPARC Walk."
            placeholderTextColor={isDark ? colors.text.secondary : "#6c757d"}
            multiline
            numberOfLines={5}
            textAlignVertical="top"
            style={[
              styles.textInput,
              error && styles.errorInput,
              isCompleted && styles.completedInput,
              isDark && {
                backgroundColor: colors.background.elevated,
                borderColor: colors.border.default,
                color: colors.text.primary,
              },
            ]}
            value={value}
            onChangeText={onChange}
            editable={!isDisabled}
            selectTextOnFocus={!isDisabled}
          />
          {error && (
            <Text style={[styles.errorText, isDark && { color: colors.status.error }]}> 
              Please provide possible solutions
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginTop: 24,
  },
  cardContainer: {
    borderRadius: 6,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
  },
  sectionHeader: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  header1text: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  container: {
    paddingTop: 12,
    paddingBottom: 10,
    borderColor: "#e9ecef",
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 6,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    backgroundColor: "white",
    padding: 16,
  },
  textInput: {
    width: "100%",
    minHeight: 40,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#fff",
    color: "#2D2D2D",
    fontFamily: FontFamily.regular,
  },
  errorInput: {
    borderColor: "#dc3545",
    backgroundColor: "#fff8f8",
  },
  completedInput: {
    backgroundColor: "#D0D0D0",
  },
  errorText: {
    color: "#dc3545",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 12,
    fontFamily: FontFamily.regular,
  },
});

export default SparcWalkInspectionPosibleSolutions;
